import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useRef, useState } from "react";
import gaanaService from "../../services/desiriGaana/gaanaservice";
import desiriUserService from "../../services/desiriUser/userservice";

interface Props {
  onSearch: (text: string) => void;
  onRefresh: () => void;
}

const DesiriGaanaSearch: React.FC<Props> = ({ onSearch, onRefresh }) => {
  const toast = useRef<Toast>(null);
  const [searchText, setSearchText] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [vendorOptions, setVendorOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [MUOptions, setMUOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [formData, setFormData] = useState({
    gaanaNumber: null as number | null,
    gaanaName: "",
    purchasedBy: "",
    purchasedDate: null as Date | null,
    price: null as number | null,
    installedDate: null as Date | null,
    posID: null as number | null,
    isOperational: false,
    lastServicedDate: null as Date | null,
  });

  const showToast = (
    summary: string,
    detail: string,
    severity: "success" | "error" | "warn"
  ) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const handleSave = async () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.gaanaNumber)
      newErrors.gaanaNumber = "Gaana Number is required";
    if (!formData.gaanaName) newErrors.gaanaName = "Gaana Name is required";
    if (!formData.purchasedBy)
      newErrors.purchasedBy = "Purchased By is required";
    if (!formData.purchasedDate)
      newErrors.purchasedDate = "Purchased Date is required";
    if (!formData.price) newErrors.price = "Price is required";
    if (!formData.posID) newErrors.posID = "Manufacturing Unit is required";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const formatDate = (date: Date | null) =>
      date ? new Date(date).toLocaleDateString("en-CA") : null;

    const payload = {
      gaanaNumber: formData.gaanaNumber,
      gaanaName: formData.gaanaName,
      purchasedBy: formData.purchasedBy,
      purchasedDate: formatDate(formData.purchasedDate),
      installedDate: formatDate(formData.installedDate),
      lastServicedDate: formatDate(formData.lastServicedDate),
      price: formData.price,
      posID: formData.posID,
      isOperational: formData.isOperational,
    };

    try {
      const res = await gaanaService.addGaana(payload);
      if (res.isSuccess) {
        showToast("Success", res.message, "success");
        setShowDialog(false);
        onRefresh();
      } else {
        showToast("Error", res.message, "error");
      }
    } catch (err) {
      showToast("Error", "Server error", "error");
    }
  };

  const fieldHoverStyle = (fieldName: string) => ({
    border:
      hoveredField === fieldName ? "2px solid #6366F1" : "1px solid #ced4da",

    boxShadow:
      hoveredField === fieldName ? "0 0 6px rgba(99, 102, 241, 0.3)" : "none",
    transition: "all 0.2s ease-in-out",
    width: "100%",
    minHeight: "40px",
  });


  const aliasMU = (id: number, name?: string) => {
    if (id === 3) return "Unit-1-KR";  
    if (id === 4) return "Unit-2-MT";  
    const up = (name ?? "").toUpperCase().replace(/\s+/g, " ");
    if (/\bK\s*R\s*NAGAR\b/.test(up) || up.includes("KR NAGAR")) return "Unit-1-KR";
    if (up.includes("MAGADI TALUK") || up.includes("MAGADI")) return "Unit-2-MT";
    return name ?? "";
  };

  useEffect(() => {
    const fetchMUPlants = async () => {
      try {
        const response = await desiriUserService.getAllMUPlants();

        const formatted = (response ?? []).map((d: any) => {
          const id = Number(d?.posID ?? d?.PosID ?? d?.id);
          const name = d?.posName ?? d?.PosName ?? d?.address2 ?? d?.Address2 ?? "";
          return {
            label: aliasMU(id, name), 
            value: id,
          };
        });
        setMUOptions(formatted);
      } catch (error) {
        console.error("Failed to load Manufacturing Units:", error);
      }
    };

    fetchMUPlants();
  }, []);
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await gaanaService.getAllVendors();
        const formatted = response.map((vendor: any) => ({
          label: vendor.firmInfo,
          value: vendor.vendorID,
        }));
        setVendorOptions(formatted);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      }
    };

    fetchVendors();
  }, []);
  const resetForm = () => {
    setFormData({
      gaanaNumber: null,
      gaanaName: "",
      purchasedBy: "",
      purchasedDate: null,
      price: null,
      installedDate: null,
      posID: null,
      isOperational: false,
      lastServicedDate: null,
    });
    setErrors({});
  };

  const handleDialogClose = () => {
    resetForm();
    setShowDialog(false);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[field];
      return updatedErrors;
    });
  };

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <div className="card border-noround p-2" style={{ minHeight: "3.5rem" }}>
        <div
          className="input-container font-bold text-white border-round"
          style={{ float: "left", width: "18%" }}
        >
          <div className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              type="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search Gaana"
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <div className="buttons-container">
          <div
            className="filter-button font-bold text-white border-round"
            style={{ float: "left", width: "10%" }}
          >
            <Button
              className="text-black border-round"
              style={{ width: "100%" }}
              onClick={() => onSearch(searchText)}
              severity="success"
            >
              Filter
            </Button>
          </div>
          <div
            className="filter-button font-bold text-white border-round ml-2"
            style={{ float: "left", width: "10%" }}
          >
            <Button
              className="text-black border-round"
              style={{ width: "100%" }}
              onClick={() => {
                setSearchText("");
                onSearch("");
              }}
              severity="secondary"
              text
              raised
            >
              Reset
            </Button>
          </div>
          <div
            className="add-button-container border-round font-bold ml-3 text-primary"
            style={{ textAlign: "right" }}
          >
            <Button
              className="bg-blue-700 border-round p-button p-button-text p-component text-white"
              onClick={() => setShowDialog(true)}
            >
              Add Gaana
            </Button>
          </div>
        </div>
      </div>

      <Dialog
        visible={showDialog}
        onHide={handleDialogClose}
        className="quickview w-50"
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        modal
      >
        <h5 className="border-round mb-3 order-top-color p-2 panel-header-text text-center">
          ADD GAANA
        </h5>
        <div className="grid">
          <div className="col-12 mb-2">
            <div className="grid">
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Manufacturing Unit<span className="required">*</span>
                </label>
                <Dropdown
                  value={formData.posID}
                  options={MUOptions}
                  onChange={(e) => handleInputChange("posID", e.value)}
                  placeholder="Select Manufacturing Unit"
                  style={fieldHoverStyle("posID")}
                />
                {errors.posID && (
                  <small className="p-error">{errors.posID}</small>
                )}
              </div>
              {/* Gaana Number */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Gaana Number
                  <span className="required">*</span>
                </label>
                <InputNumber
                  value={formData.gaanaNumber}
                  onChange={(e) =>
                    handleInputChange("gaanaNumber", e.value ?? null)
                  }
                  useGrouping={false}
                  placeholder="Enter Gaana Number"
                  style={fieldHoverStyle("gaanaNumber")}
                  onKeyPress={(e) => {
                    const currentValue = formData.gaanaNumber?.toString() || "";
                    if (!/[0-9]/.test(e.key) || currentValue.length >= 10) {
                      e.preventDefault();
                    }
                  }}
                />
                {errors.gaanaNumber && (
                  <small className="p-error">{errors.gaanaNumber}</small>
                )}
              </div>

              {/* Gaana Name */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Gaana Name <span className="required">*</span>
                </label>
                <InputText
                  value={formData.gaanaName}
                  onChange={(e) =>
                    handleInputChange("gaanaName", e.target.value)
                  }
                  style={fieldHoverStyle("gaanaName")}
                  placeholder="Enter Gaana Name"
                />
                {errors.gaanaName && (
                  <small className="p-error">{errors.gaanaName}</small>
                )}
              </div>

              {/* Purchased By */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Purchased By<span className="required">*</span>
                </label>
                <Dropdown
                  value={formData.purchasedBy}
                  options={vendorOptions}
                  onChange={(e) => handleInputChange("purchasedBy", e.value)}
                  filter
                  placeholder="Select Vendor"
                  style={fieldHoverStyle("purchasedBy")}
                />
                {errors.purchasedBy && (
                  <small className="p-error">{errors.purchasedBy}</small>
                )}
              </div>

              {/* Purchased Date */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Purchased Date<span className="required">*</span>
                </label>
                <Calendar
                  value={formData.purchasedDate}
                  onChange={(e) => {
                    const newDate = e.value instanceof Date ? e.value : null;
                    setFormData((prev) => ({
                      ...prev,
                      purchasedDate: newDate,
                    }));

                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      purchasedDate: "",
                    }));
                  }}
                  placeholder="Select Purchased Date"
                  dateFormat="dd-mm-yy"
                  style={fieldHoverStyle("purchasedDate")}
                />
                {errors.purchasedDate && (
                  <small className="p-error">{errors.purchasedDate}</small>
                )}

                {errors.purchasedDate && (
                  <small className="p-error">{errors.purchasedDate}</small>
                )}
              </div>

              {/* Price */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Price<span className="required">*</span>
                </label>
                <InputNumber
                  value={formData.price}
                  onChange={(e) => handleInputChange("price", e.value ?? null)}
                  placeholder="Enter Price"
                  style={fieldHoverStyle("price")}
                />
                {errors.price && (
                  <small className="p-error">{errors.price}</small>
                )}
              </div>

              {/* Installed Date */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">Installed Date</label>
                <Calendar
                  value={formData.installedDate}
                  onChange={(e) => {
                    const newDate = e.value instanceof Date ? e.value : null;
                    setFormData((prev) => ({
                      ...prev,
                      installedDate: newDate,
                    }));

                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      installedDate: "",
                    }));
                  }}
                  placeholder="Select Installed Date"
                  dateFormat="dd-mm-yy"
                  style={fieldHoverStyle("installedDate")}
                />
              </div>

              {/* Is Operational */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">
                  Is Operational?<span className="required">*</span>
                </label>
                <Dropdown
                  value={formData.isOperational}
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                  onChange={(e) =>
                    setFormData({ ...formData, isOperational: e.value })
                  }
                  placeholder="Select"
                  style={fieldHoverStyle("isOperational")}
                />
                {errors.isOperational && (
                  <small className="p-error">{errors.isOperational}</small>
                )}
              </div>

              {/* Last Serviced Date */}
              <div className="field col-12 md:col-6">
                <label className="label-semi-bold">Last Serviced Date</label>
                <Calendar
                  value={formData.lastServicedDate}
                  onChange={(e) => {
                    const newDate = e.value instanceof Date ? e.value : null;
                    setFormData((prev) => ({
                      ...prev,
                      lastServicedDate: newDate,
                    }));

                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      lastServicedDate: "",
                    }));
                  }}
                  placeholder="Select Last Serviced Date"
                  dateFormat="dd-mm-yy"
                  style={fieldHoverStyle("lastServicedDate")}
                />
              </div>

              <div className="field col-12 md:col-6 text-right  mt-5">
                <Button
                  className="bg-green-700 text-white p-button p-button-text p-component"
                  onClick={handleSave}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default DesiriGaanaSearch;
